import type { AlbumDocument, TemplateId } from "../domain/project";

export interface ShowcaseEntry {
  id: TemplateId;
  name: string;
  eyebrow: string;
  description: string;
  cue: string;
}

export type DemoCollection = "landscape" | "people";

export interface DemoPhoto {
  fileName: string;
  assetId: string;
  title: string;
  caption: string;
  location: string;
}

const showcaseUrl = (name: string) => `${import.meta.env.BASE_URL}showcase/${name}`;

export const SHOWCASE_URLS: Record<string, string> = {
  "landscape-01": showcaseUrl("landscape-01.webp"),
  "landscape-02": showcaseUrl("landscape-02.webp"),
  "landscape-03": showcaseUrl("landscape-03.webp"),
  "portrait-01": showcaseUrl("portrait-01.webp"),
  "portrait-02": showcaseUrl("portrait-02.webp"),
  "portrait-03": showcaseUrl("portrait-03.webp"),
};

const landscapePresentation = { fit: "contain", frame: "none", spacing: "compact" } as const;

const landscapePages = [
  { id: "showcase-page-01", assetId: "landscape-01", title: "林间回声", caption: "一条路把视线带进更深的绿色。", location: "林间 / 09:12", presentation: landscapePresentation },
  { id: "showcase-page-02", assetId: "landscape-02", title: "夜色停靠", caption: "山脊把夜空托得很低，星光因此有了重量。", location: "旷野 / 22:48", presentation: landscapePresentation },
  { id: "showcase-page-03", assetId: "landscape-03", title: "月亮经过", caption: "天空褪成纸上的两种颜色，故事也慢了下来。", location: "高地 / 05:36", presentation: landscapePresentation },
];

const peoplePages = [
  { id: "showcase-page-04", assetId: "portrait-01", title: "风从水面来", caption: "把围巾拢紧以后，仍然愿意在风里多站一会儿。", location: "海边 / 冬日" },
  { id: "showcase-page-05", assetId: "portrait-02", title: "光落在脸上", caption: "一束自然光，替这段沉默写下了标题。", location: "窗边 / 14:20" },
  { id: "showcase-page-06", assetId: "portrait-03", title: "留给下一页", caption: "没有说完的话，先和照片一起被保存。", location: "室内 / 23:06" },
];

type ShowcasePage = AlbumDocument["pages"][number];

export const SHOWCASE_COLLECTIONS: Record<DemoCollection, { label: string; description: string; pages: readonly ShowcasePage[] }> = {
  landscape: { label: "风景影集", description: "山、路和天空被放在同一段旅程里。", pages: landscapePages },
  people: { label: "人物影集", description: "让人物和人物相遇，保留每一张脸的完整轮廓。", pages: peoplePages },
};

const coverByTemplate: Record<TemplateId, number> = {
  paper: 0,
  timeline: 1,
  gallery: 2,
  book: 1,
  cinema: 2,
  deck: 0,
  focus: 1,
  scrapbook: 2,
  accordion: 1,
  drift: 2,
  circular: 0,
  depth: 1,
};

export function getShowcaseDocument(template: TemplateId, collection: DemoCollection): AlbumDocument {
  const selected = SHOWCASE_COLLECTIONS[collection];
  const coverIndex = coverByTemplate[template] % selected.pages.length;
  return {
    title: collection === "people" ? "与人相遇的几页" : "一段被留下的光",
    subtitle: collection === "people" ? "把人物放在一起，故事才有了呼吸。" : "同一组风景，也可以被读成不同的故事。",
    date: "2026",
    location: collection === "people" ? "PhotoFolio / people" : "PhotoFolio / landscape",
    template,
    coverAssetId: selected.pages[coverIndex]?.assetId,
    pages: [...selected.pages],
  };
}

export const SHOWCASE_DOCUMENT: AlbumDocument = {
  title: "一段被留下的光",
  subtitle: "同一组照片，也可以被读成不同的故事。",
  date: "2026",
  location: "PhotoFolio showcase",
  template: "book",
  coverAssetId: "landscape-02",
  pages: landscapePages,
};

export const DEPTH_SHOWCASE_DOCUMENT: AlbumDocument = {
  title: "记忆仍在呼吸",
  subtitle: "普通照片也可以拥有一点空间感。",
  date: "2026",
  location: "PhotoFolio / depth photo",
  template: "gallery",
  coverAssetId: "landscape-02",
  pages: [...landscapePages, ...peoplePages],
};

export const DEMO_PHOTOS: Record<DemoCollection, readonly DemoPhoto[]> = {
  landscape: landscapePages.map((page) => ({
    fileName: `${page.assetId}.webp`,
    assetId: page.assetId,
    title: page.title,
    caption: page.caption,
    location: page.location,
  })),
  people: peoplePages.map((page) => ({
    fileName: `${page.assetId}.webp`,
    assetId: page.assetId,
    title: page.title,
    caption: page.caption,
    location: page.location,
  })),
};

export const SHOWCASE_ENTRIES: readonly ShowcaseEntry[] = [
  { id: "book", name: "真实相册", eyebrow: "翻页 / BOOK", description: "让每一张照片拥有正反两面，像一本可以慢慢翻完的纸质影集。", cue: "左右滑动或使用按钮翻页" },
  { id: "paper", name: "纸上叙事", eyebrow: "留白 / PAPER", description: "用克制的留白、标题和说明，让照片之间的顺序成为故事。", cue: "向下阅读" },
  { id: "timeline", name: "时间线", eyebrow: "经过 / TIMELINE", description: "按发生的顺序排列，把一段旅程读成连续的时间。", cue: "沿时间向前" },
  { id: "gallery", name: "沉浸画廊", eyebrow: "观看 / GALLERY", description: "让照片成为主角，适合一次安静而完整的浏览。", cue: "滚动浏览更多照片" },
  { id: "cinema", name: "电影放映", eyebrow: "节奏 / CINEMA", description: "一张一张出现，像记忆在黑暗里被重新点亮。", cue: "开始放映" },
  { id: "deck", name: "照片卡牌", eyebrow: "翻转 / DECK", description: "把照片叠成一沓，轻轻拨开一张，再遇见下一张。", cue: "拨动卡片" },
  { id: "focus", name: "聚焦墙", eyebrow: "聚焦 / FOCUS", description: "以一张大图为中心，保留一面可探索的照片墙。", cue: "点开细节" },
  { id: "scrapbook", name: "手作剪贴", eyebrow: "收藏 / SCRAPBOOK", description: "像把旅行中的票根和照片贴在桌面上，留下不规则的温度。", cue: "靠近查看" },
  { id: "accordion", name: "手风琴图库", eyebrow: "展开 / ACCORDION", description: "让照片像一排折叠的画幅，点开一张就把完整画面交给你。", cue: "悬停或点按展开" },
  { id: "drift", name: "漂移墙", eyebrow: "漂移 / DRIFT WALL", description: "五张以上照片在一面墙上缓慢错开，像记忆还没有完全停下来。", cue: "移入暂停，离开继续" },
  { id: "circular", name: "环形图库", eyebrow: "旋转 / CIRCULAR", description: "把照片放进一圈有深度的空间，拖动或点按，让下一张来到面前。", cue: "拖动卡片旋转" },
  { id: "depth", name: "活照片", eyebrow: "深度 / LIVING PHOTO", description: "普通照片也能回应指针，透视、光泽与轻微视差一起呼吸。", cue: "移入照片查看深度" },
];

export const SHOWCASE_ASSET_IDS = Object.keys(SHOWCASE_URLS);
